import React, { useEffect, useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import Header from "components/Header";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  useGetReferralMessageQuery,
  useUpdateReferralMessageMutation,
} from "state/api";

const emptyForm = {
  referralPageTitle: "",
  referralHeadline: "",
  referralBody: "",
  referralShareMessage: "",
};

const ReferralMessageSettings = () => {
  const { data, isLoading, isError } = useGetReferralMessageQuery();
  const [updateMessage, { isLoading: isSaving }] =
    useUpdateReferralMessageMutation();
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState({ type: "", text: "" });

  useEffect(() => {
    if (!data) return;
    setForm({
      referralPageTitle: data.referralPageTitle || "",
      referralHeadline: data.referralHeadline || "",
      referralBody: data.referralBody || "",
      referralShareMessage: data.referralShareMessage || "",
    });
  }, [data]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setStatus({ type: "", text: "" });
    const payload = {
      referralPageTitle: form.referralPageTitle.trim(),
      referralHeadline: form.referralHeadline.trim(),
      referralBody: form.referralBody.trim(),
      referralShareMessage: form.referralShareMessage.trim(),
    };
    if (
      !payload.referralPageTitle ||
      !payload.referralHeadline ||
      !payload.referralBody ||
      !payload.referralShareMessage
    ) {
      setStatus({ type: "error", text: "All fields are required." });
      return;
    }
    try {
      await updateMessage(payload).unwrap();
      setStatus({
        type: "success",
        text: "Referral content saved. App shows it on next open of Refer & Earn.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        text: error?.data?.message || "Failed to save content.",
      });
    }
  };

  return (
    <div className="p-4 md:p-6">
      <Header
        title="Referral content"
        subtitle="Page title, headline, description, and share text for Refer & Earn"
      />

      {status.text && (
        <p
          className={`mt-4 text-sm ${
            status.type === "success" ? "text-green-600" : "text-red-600"
          }`}
        >
          {status.text}
        </p>
      )}

      <Card className="mt-6 max-w-3xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Refer & Earn content
          </CardTitle>
          <CardDescription>
            These texts appear on the app Refer & Earn screen. For share text,
            put <code className="rounded bg-muted px-1">{`{link}`}</code> where
            the invite URL should go.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          ) : isError ? (
            <p className="text-sm text-red-600">Failed to load content.</p>
          ) : (
            <>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Page title</span>
                <input
                  type="text"
                  value={form.referralPageTitle}
                  onChange={(e) =>
                    setField("referralPageTitle", e.target.value)
                  }
                  maxLength={80}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="Refer and Earn"
                />
              </label>

              <label className="block space-y-1">
                <span className="text-sm font-medium">Headline</span>
                <textarea
                  value={form.referralHeadline}
                  onChange={(e) => setField("referralHeadline", e.target.value)}
                  rows={2}
                  maxLength={200}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="Invite Friends, Earn Bonus Wishes..."
                />
                <span className="text-xs text-muted-foreground">
                  {form.referralHeadline.length}/200
                </span>
              </label>

              <label className="block space-y-1">
                <span className="text-sm font-medium">Description</span>
                <textarea
                  value={form.referralBody}
                  onChange={(e) => setField("referralBody", e.target.value)}
                  rows={4}
                  maxLength={1000}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="Share your link or code..."
                />
                <span className="text-xs text-muted-foreground">
                  {form.referralBody.length}/1000
                </span>
              </label>

              <label className="block space-y-1">
                <span className="text-sm font-medium">Share message</span>
                <textarea
                  value={form.referralShareMessage}
                  onChange={(e) =>
                    setField("referralShareMessage", e.target.value)
                  }
                  rows={6}
                  maxLength={2000}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="Invite friends… Link {link}"
                />
                <span className="text-xs text-muted-foreground">
                  {form.referralShareMessage.length}/2000 · use {"{link}"} for
                  invite URL
                </span>
              </label>

              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save content"
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralMessageSettings;
